import { getServiceUrl } from '../api.config';
import {
  ResponseWithBody,
  RuleSetAdditionalInfos,
  ValidationFilters,
} from '../types';
import { ApiClientOptions } from '../types/api';
import ApiClient from '../utils/ApiClient';
import qs from '../utils/query';

type ComplianceReportAPIParams =
  | {
      type: 'by_pkids';
      payload: {
        format: 'csv' | 'xlsx';
        product_key_ids: number[];
      };
    }
  | {
      type: 'by_filters';
      payload: {
        format: 'csv' | 'xlsx';
        advanced_search: unknown;
        queryName?: string;
      };
    };

export default class ValidationApi extends ApiClient {
  public constructor(options?: ApiClientOptions) {
    super(getServiceUrl('validation'), options);
  }

  // @TODO CCS: use product key id only
  public bypassRule(
    ruleId: number,
    productId: number,
    organizationId: number,
    productKeyId: number,
  ) {
    const query = `/product/v2/rules/bypass`;
    return this.post(query, {
      data: {
        product_id: productId,
        organization_id: organizationId,

        id: productKeyId, // Not used by the back

        rule_id: ruleId,
        bypassed: true,
      },
    });
  }

  // @TODO CCS: use product key id only
  public unbypassRule(
    ruleId: number,
    productId: number,
    organizationId: number,
    productKeyId: number,
  ) {
    const query = `/product/v2/rules/bypass`;
    return this.post(query, {
      data: {
        product_id: productId,
        organization_id: organizationId,

        id: productKeyId, // Not used by the back

        rule_id: ruleId,
        bypassed: false,
      },
    });
  }

  public applyRulesRequired(data: any) {
    const query = '/validation/v3/rules/required';
    return this.post(query, data);
  }

  public applyOneRule(rule_id: number, payload: any) {
    const query = `/validation/v3/rules/${rule_id}/apply`;
    const data = { data: { ...payload, _type: 'ProductVersion' } };
    return this.post(query, data);
  }

  public validateProduct(data: any) {
    const query = '/validation/v3/product/validate';
    return this.post(query, data);
  }

  // Admin routes

  public listRuleSets(search?: string, filters?: any) {
    const searchQuery: any = {};

    if (search) {
      searchQuery.search = search;
    }
    if (filters?.ruleSetTypeIn?.length) {
      searchQuery.rule_set_type_in = filters.ruleSetTypeIn;
    }
    if (filters?.organizationIdIn?.length) {
      searchQuery.organization_id_in = filters.organizationIdIn;
    }

    return this.get(`/validation/v3/rulesets?${qs.stringify(searchQuery)}`);
  }

  public listRuleSetsIn(rulesetIds: number[]) {
    const searchQuery: any = {};
    if (rulesetIds.length) {
      searchQuery.rule_set_id_in = rulesetIds;
    }
    return this.get(`/validation/v3/rulesets?${qs.stringify(searchQuery)}`);
  }

  public listInfosRuleSetsIn(rulesetIds: number[]) {
    const searchQuery: any = {};
    if (rulesetIds.length) {
      searchQuery.rule_set_id_in = rulesetIds;
    }
    return this.get(
      `/validation/v3/rulesets/infos?${qs.stringify(searchQuery)}`,
    );
  }

  public createRuleSet(data: any) {
    return this.post('/validation/v3/rulesets', data);
  }

  public updateRuleSet(id: string | number, data: any) {
    return this.put(`/validation/v3/rulesets/${id}`, data);
  }

  public deleteRuleSet(id: string | number) {
    return this.delete(`/validation/v3/rulesets/${id}`);
  }

  public fetchRuleSet(id: number, additional_infos?: RuleSetAdditionalInfos) {
    const searchQuery: any = additional_infos || {};

    return this.get(
      `/validation/v3/rulesets/${id}${qs.stringify(searchQuery, true)}`,
    );
  }

  public listCachedRules(filters: ValidationFilters) {
    const {
      ruleId,
      ruleIdIn,
      ruleSetId,
      ruleSetIdIn,
      ruleSetTypeIn,
      entityType,
      entityTypeIn,
      fieldEntityTypeIn,
      status,
      applicableForKinds,
      appliesOnPkId,
      usedByAnyRetailerIdIn,
      usedByAllRetailerIdIn,
      filterUnsolvableRules,
      applyDraftRuleSets,
      search,
      isCustom,
      isDocumented,
      offset,
      limit,
      sort,
      onlyActive,
    } = filters;
    const queryDict: any = {};
    if (ruleId) {
      queryDict.rule_id_in = [ruleId];
    }
    if (ruleIdIn?.length) {
      queryDict.rule_id_in = ruleIdIn;
    }
    if (ruleSetId) {
      queryDict.rule_set_id = [ruleSetId];
    }
    if (ruleSetIdIn?.length) {
      queryDict.rule_set_id_in = ruleSetIdIn;
    }
    if (ruleSetTypeIn?.length) {
      queryDict.rule_set_type_in = ruleSetTypeIn;
    }
    if (entityType) {
      queryDict.rule_entity_type_in = [entityType];
    }
    if (entityTypeIn?.length) {
      queryDict.rule_entity_type_in = entityTypeIn;
    }
    if (fieldEntityTypeIn?.length) {
      queryDict.field_entity_type_in = fieldEntityTypeIn;
    }
    if (status !== undefined) {
      queryDict.status = status;
    }
    if (applicableForKinds?.length) {
      queryDict.applicable_for_kinds = applicableForKinds;
    }
    if (appliesOnPkId) {
      queryDict.applies_on_product_key_id = appliesOnPkId;
    }
    if (usedByAnyRetailerIdIn?.length) {
      queryDict.used_by_any_retailer_id_in = usedByAnyRetailerIdIn;
    }
    if (usedByAllRetailerIdIn?.length) {
      queryDict.used_by_all_retailer_id_in = usedByAllRetailerIdIn;
    }
    if (filterUnsolvableRules !== undefined) {
      queryDict.filter_unsolvable_rules = filterUnsolvableRules;
    }
    if (applyDraftRuleSets !== undefined) {
      queryDict.apply_draft_rulesets = applyDraftRuleSets;
    }
    if (search?.length) {
      queryDict.search = search;
    }
    if (isCustom !== undefined) {
      queryDict.is_custom = isCustom;
    }
    if (isDocumented !== undefined) {
      queryDict.is_documented = isDocumented;
    }
    if (offset !== undefined) {
      queryDict.offset = offset;
    }
    if (limit !== undefined) {
      queryDict.limit = limit;
    }
    if (sort?.length) {
      queryDict.sort = sort;
    }
    if (onlyActive) {
      queryDict.only_active = onlyActive;
    }

    return this.get(`/validation/v4/rules${qs.stringify(queryDict, true)}`);
  }

  public listRules(filters: ValidationFilters) {
    return this.get(
      `/validation/v3/rules${qs.stringify(
        { rule_set_id: filters.ruleSetId },
        true,
      )}`,
    );
  }

  public createRuleFromTemplate(data: any) {
    const query = '/validation/v3/rules/templated';
    return this.post(query, data);
  }

  public fetchRule(ruleId: string | number) {
    return this.get(`/validation/v3/rules/${ruleId}`);
  }

  public updateRule(ruleId: string | number, data: any) {
    return this.put(`/validation/v3/rules/${ruleId}`, data);
  }

  public moveRulesToRuleset(
    ruleIds: string[] | number[],
    rulesetId: string | number,
    synchronous: boolean,
  ) {
    return this.put(
      `/validation/v3/rules/to_ruleset${qs.stringify({ synchronous }, true)}`,
      {
        rule_ids: ruleIds,
        ruleset_id: rulesetId,
      },
    );
  }

  public deleteRules(ruleIds: string[] | number[], synchronous: boolean) {
    return this.delete(
      `/validation/v3/rules${qs.stringify({ synchronous }, true)}`,
      {
        rule_ids: ruleIds,
      },
    );
  }

  public duplicateRulesInRuleset(
    ruleIds: string[] | number[],
    rulesetId: string | number,
    synchronous: boolean,
  ) {
    return this.post(
      `/validation/v4/rules/duplicate${qs.stringify({ synchronous }, true)}`,
      {
        rules_id: ruleIds,
        target_ruleset_id: rulesetId,
      },
    );
  }

  public fetchRuleDimensions(data: any) {
    return this.post(`/validation/v4/rule/dimensions`, data);
  }

  public deleteRule(ruleId: string | number) {
    return this.delete(`/validation/v3/rules/${ruleId}`);
  }

  public getOrganizationRules(organizationId: number) {
    return this.get(`/validation/v3/organizations/${organizationId}`);
  }

  public upsertOrganizationRules(organizationId: number, data?: any) {
    return this.post(`/validation/v3/organizations/${organizationId}`, data);
  }

  public refreshCache() {
    return this.post('/validation/v3/rules/cache/refresh');
  }

  public listRuleFields(templateTypes: string[] = []) {
    return this.get(
      '/validation/v3/rules/fields',
      qs.stringify({ template_types: templateTypes.join(',') }, true),
    );
  }

  public filterRequiredFields(fields: any[] = []) {
    return this.post('/validation/v3/rules/filter_required', {
      data: {
        fields,
      },
    });
  }

  public listRequiredFields(
    targetOrganizationIds: number[],
    onlyBlocking: boolean = true,
  ) {
    return this.post('/validation/v4/fields/required', {
      only_blocking: onlyBlocking,
      target_organization_ids: targetOrganizationIds.join(','),
    });
  }

  public updateRuleSetsRequireStatus(
    ruleSetValues: { [key: number]: boolean },
    productKeyIds: number[],
    deadline: string | null = null, // Datetime in ISO format (WITH TZ)
  ) {
    const body: {
      ruleset_values: { [key: number]: boolean };
      entity_ids: number[];
      entity_type: string;
      deadline: string | null;
    } = {
      ruleset_values: ruleSetValues,
      entity_ids: productKeyIds,
      entity_type: 'PRODUCT_KEY',
      deadline,
    };
    return this.post('/validation/v3/rulesets/require', body);
  }

  public getRequestableRulesets() {
    return this.get('/validation/v3/rulesets/requestable');
  }

  public getComplianceReport({ payload }: ComplianceReportAPIParams) {
    const query = '/validation/v4/compliance_report';
    return this.getBlob<Blob, ResponseWithBody<Blob>>(query, '', payload, true);
  }

  // ##########################################################################
  //                            Normalized comments
  // ##########################################################################

  public createNormalizedComment(data: any) {
    const query = '/validation/v3/normalizedcomment';
    return this.post(query, data);
  }

  public bulkCreateNormalizedComment(data: any) {
    const query = '/validation/v3/normalizedcomment/bulk';
    return this.post(query, data);
  }

  public dismissNormalizedComment(id: string | number) {
    const query = `/validation/v3/normalizedcomment/dismiss/${id}`;
    return this.get(query);
  }
}
